import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { Quaternion } from '../../types/Quaternion';
import { LayoutData, SpacingMode } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';

export class GridLayouter extends BaseLayouter {
    public readonly defaultSpacing: number = 3.0;
    public readonly defaultRows: number = 2;
    public readonly defaultCols: number = 3;
    
    constructor() {
        super(
            'grid',
            'Arranges objects in a 3D grid pattern with configurable rows, columns, and spacing',
            {
                gridFixedSpacing: 3.0,
                gridRows: 2,
                gridCols: 3,
                gridOffsetX: 0,
                gridOffsetY: 0,
                gridOffsetZ: 0,
                alignmentMode: AlignmentMode.Aligned
            },
            '2d', // Grid is 2D
            [SpacingMode.Fixed, SpacingMode.Distribute] // Support both spacing modes
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const spacing = this.getEffectiveSpacing(layoutData, totalCount);
        const rows = this.getParameter(layoutData, 'gridRows', this.defaultRows);
        const cols = this.getParameter(layoutData, 'gridCols', this.defaultCols);
        const offsetX = this.getParameter(layoutData, 'gridOffsetX', 0);
        const offsetY = this.getParameter(layoutData, 'gridOffsetY', 0);
        const offsetZ = this.getParameter(layoutData, 'gridOffsetZ', 0);
        
        // All objects arranged in grid (including index 0)
        const adjustedIndex = index;
        const row = Math.floor(adjustedIndex / cols);
        const col = adjustedIndex % cols;
        
        const x = (col - (cols - 1) / 2) * spacing + offsetX;
        const y = offsetY;
        const z = (row - (rows - 1) / 2) * spacing + offsetZ;
        
        return new Vector3(x, y, z);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        // All objects get proper orientation (including index 0)
        
        const position = this.calculatePosition(index, totalCount, layoutData);
        
        // Use the new quaternion-based orientation calculation
        return this.orientationToEulerAngles(
            position, index, totalCount, layoutData
        );
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        // CONSISTENT FIRST ORIENTATION: Index 0 has no rotation
        if (index === 0) {
            return Quaternion.identity();
        }
        
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate surface frame for 2D grid plane
        const surfaceFrame = this.calculateGridSurfaceFrame(position, layoutData);
        
        // Determine forward and up directions using alignment system
        const desiredForward = this.determineForwardDirection(alignmentMode, undefined, surfaceFrame);
        const desiredUp = this.determineUpDirection(upVectorMode, undefined, surfaceFrame);
        
        // Build orthogonal coordinate system
        const { finalRight, finalUp, finalForward } = this.buildOrthogonalSystem(
            desiredForward, desiredUp, upVectorMode
        );
        
        // Apply special correction for tilted plane + aligned + path up (as per docs)
        if (alignmentMode === AlignmentMode.Aligned && upVectorMode === UpVectorMode.PathUp) {
            return this.applyGridAlignedPathUpCorrection(finalForward, finalUp, finalRight);
        }
        
        // Create quaternion from orthogonal system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Calculate surface frame for grid plane (2D surface)
     * Grid is a planar surface in XZ-plane with normal pointing up in Y
     */
    private calculateGridSurfaceFrame(position: Vector3, layoutData: LayoutData): {
        uTangent: Vector3,
        vTangent: Vector3,
        normal: Vector3
    } {
        // For a standard grid plane (XZ-plane):
        // U-tangent points along X-axis (column direction)
        const uTangent = new Vector3(1, 0, 0);
        
        // V-tangent points along Z-axis (row direction)  
        const vTangent = new Vector3(0, 0, 1);
        
        // Surface normal points up along Y-axis
        const normal = new Vector3(0, 1, 0);
        
        return { uTangent, vTangent, normal };
    }
    
    /**
     * Apply special correction for grid aligned + path up mode
     * This implements the special case mentioned in the documentation
     */
    private applyGridAlignedPathUpCorrection(
        forward: Vector3, 
        up: Vector3, 
        right: Vector3
    ): Quaternion {
        // Create base quaternion
        let quaternion = Quaternion.lookDir(forward, up);
        
        // Apply corrections as described in documentation for aligned + path up on planes
        // Note: For a flat grid, this might not need corrections, but we preserve the pattern
        // for consistency with tilted planes
        
        return quaternion;
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'gridFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        const width = this.getParameter(layoutData, 'gridWidth', 10.0);
        const height = this.getParameter(layoutData, 'gridHeight', 10.0);
        const rows = this.getParameter(layoutData, 'gridRows', this.defaultRows);
        const cols = this.getParameter(layoutData, 'gridCols', this.defaultCols);
        
        // Calculate spacing based on grid dimensions and object count
        const spacingX = rows > 1 ? width / (cols - 1) : width;
        const spacingZ = cols > 1 ? height / (rows - 1) : height;
        
        // Use the smaller spacing to ensure objects fit within bounds
        return Math.min(spacingX, spacingZ);
    }
    
    validateGridParameters(layoutData: LayoutData): boolean {
        const spacing = this.getEffectiveSpacing(layoutData, 1);
        const rows = this.getParameter(layoutData, 'gridRows', this.defaultRows);
        const cols = this.getParameter(layoutData, 'gridCols', this.defaultCols);
        
        return spacing > 0 && rows > 0 && cols > 0;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        return super.validateParameters(layoutData) && this.validateGridParameters(layoutData);
    }
    
    // Calculate total grid capacity
    getGridCapacity(layoutData: LayoutData): number {
        const rows = this.getParameter(layoutData, 'gridRows', this.defaultRows);
        const cols = this.getParameter(layoutData, 'gridCols', this.defaultCols);
        return rows * cols;
    }
    
    // Calculate required layers for given object count
    getRequiredLayers(objectCount: number, layoutData: LayoutData): number {
        const capacity = this.getGridCapacity(layoutData);
        return Math.ceil(objectCount / capacity);
    }
    
    // Get grid dimensions for given object count
    getGridDimensions(objectCount: number, layoutData: LayoutData): { rows: number; cols: number; layers: number } {
        const rows = this.getParameter(layoutData, 'gridRows', this.defaultRows);
        const cols = this.getParameter(layoutData, 'gridCols', this.defaultCols);
        const layers = this.getRequiredLayers(objectCount, layoutData);
        
        return { rows, cols, layers };
    }
    
    // Clone method
    clone(): GridLayouter {
        return new GridLayouter();
    }
    
    // Factory method
    static create(): GridLayouter {
        return new GridLayouter();
    }
} 